import React, { useEffect, useMemo, useState } from "react";
import AppPageTitle from "../../components/AppPageTitle";
import MainContainer from "../../components/MainContainer";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const PAGE_SIZE = 10;

const PERIOD_OPTIONS = [
	{ value: "0", label: "Todos" },
	{ value: "1", label: "Hoje" },
	{ value: "2", label: "Ontem" },
	{ value: "3", label: "Últimos 7 dias" },
	{ value: "4", label: "Últimos 30 dias" },
	{ value: "5", label: "Este mês" },
	{ value: "6", label: "Últimos 6 meses" },
	{ value: "7", label: "Este ano" },
];

const RATING_OPTIONS = [
	{ value: 1, label: "1 estrela" },
	{ value: 2, label: "2 estrelas" },
	{ value: 3, label: "3 estrelas" },
	{ value: 4, label: "4 estrelas" },
	{ value: 5, label: "5 estrelas" },
];

function IconSearch() {
	return (
		<svg className="size-4 text-default-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<circle cx="11" cy="11" r="8" />
			<path d="m21 21-4.35-4.35" />
		</svg>
	);
}

function IconUsers() {
	return (
		<svg className="size-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
			<circle cx="9" cy="7" r="4" />
			<path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
		</svg>
	);
}

function IconSpark() {
	return (
		<svg className="size-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="m12 3 1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9L12 3Z" />
			<path d="M5 3v4" />
			<path d="M3 5h4" />
			<path d="M19 17v4" />
			<path d="M17 19h4" />
		</svg>
	);
}

function IconThumbUp() {
	return (
		<svg className="size-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M7 10v12" />
			<path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.97 2.36l-1.34 7A2 2 0 0 1 18.5 21H6a2 2 0 0 1-2-2v-9a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11l2.1-4.2A1 1 0 0 1 13.55 2H14a1 1 0 0 1 1 1v2.88Z" />
		</svg>
	);
}

function IconMessage() {
	return (
		<svg className="size-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
		</svg>
	);
}

function IconStar({ filled = false, className = "size-4", colorClassName }) {
	return (
		<svg
			className={`${className} ${colorClassName || ""}`.trim()}
			viewBox="0 0 24 24"
			fill={filled ? "currentColor" : "none"}
			stroke="currentColor"
			strokeWidth="2"
		>
			<polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
		</svg>
	);
}

function IconChevronLeft({ className = "size-4" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="m15 18-6-6 6-6" />
		</svg>
	);
}

function IconChevronRight({ className = "size-4" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="m9 18 6-6-6-6" />
		</svg>
	);
}

function getInitials(name) {
	if (!name) return "?";
	return name
		.split(" ")
		.filter(Boolean)
		.slice(0, 2)
		.map((part) => part[0]?.toUpperCase())
		.join("");
}

function formatDateTime(value) {
	if (!value) return "-";
	return new Date(value).toLocaleString("pt-BR");
}

function getRatingMeta(value) {
	return (
		RATING_OPTIONS.find((item) => item.value === Number(value)) ||
		RATING_OPTIONS[2]
	);
}

function FilterField({ label, children }) {
	return (
		<div className="space-y-2">
			<label className="block text-sm font-medium text-default-700 dark:text-default-200">
				{label}
			</label>
			{children}
		</div>
	);
}

function OverviewMetricCard({ title, value, hint, icon, toneClassName }) {
	return (
		<div className="card h-full">
			<div className="card-body flex items-start justify-between gap-4">
				<div className="min-w-0">
					<p className="mb-1 text-xs font-semibold uppercase tracking-wide text-default-500 dark:text-default-400">
						{title}
					</p>
					<p className="m-0 text-2xl font-semibold text-default-800 dark:text-default-100">
						{value}
					</p>
					{hint ? (
						<p className="mt-2 mb-0 text-sm text-default-500 dark:text-default-400">
							{hint}
						</p>
					) : null}
				</div>
				<div className={`flex size-12 shrink-0 items-center justify-center rounded-2xl ${toneClassName}`}>
					{icon}
				</div>
			</div>
		</div>
	);
}

function SectionCard({ title, subtitle, children }) {
	return (
		<div className="card h-full">
			<div className="card-body">
				<div className="mb-5">
					<p className="mb-1 text-sm font-semibold text-default-800 dark:text-default-100">
						{title}
					</p>
					{subtitle ? (
						<p className="m-0 text-sm text-default-500 dark:text-default-400">
							{subtitle}
						</p>
					) : null}
				</div>
				{children}
			</div>
		</div>
	);
}

function RankingCard({ title, subtitle, data, emptyMessage, formatter }) {
	const maxValue = data.length ? Math.max(...data.map((item) => item.value), 1) : 1;

	return (
		<SectionCard title={title} subtitle={subtitle}>
			{data.length === 0 ? (
				<p className="m-0 text-sm text-default-500 dark:text-default-400">
					{emptyMessage}
				</p>
			) : (
				<div className="space-y-4">
					{data.map((item) => {
						const width = Math.max((item.value / maxValue) * 100, item.value > 0 ? 10 : 0);
						return (
							<div key={item.name} className="space-y-2">
								<div className="flex items-center justify-between gap-3">
									<p className="m-0 truncate text-sm font-medium text-default-700 dark:text-default-200">
										{item.name}
									</p>
									<p className="m-0 shrink-0 text-xs font-semibold uppercase tracking-wide text-default-500 dark:text-default-400">
										{formatter(item)}
									</p>
								</div>
								<div className="h-2.5 overflow-hidden rounded-full bg-default-100 dark:bg-default-800">
									<div
										className="h-full rounded-full bg-primary"
										style={{ width: `${width}%` }}
									/>
								</div>
							</div>
						);
					})}
				</div>
			)}
		</SectionCard>
	);
}

function StarRatingDisplay({ value, size = "size-4", showValue = false }) {
	const ratingValue = Number(value) || 0;

	return (
		<div className="inline-flex items-center gap-2">
			<div className="inline-flex items-center gap-1">
				{[1, 2, 3, 4, 5].map((star) => (
					<IconStar
						key={star}
						filled={ratingValue >= star}
						className={size}
						colorClassName={
							ratingValue >= star
								? "text-warning"
								: "text-default-300 dark:text-default-600"
						}
					/>
				))}
			</div>
			{showValue ? (
				<span className="text-sm font-semibold text-default-700 dark:text-default-200">
					{ratingValue.toFixed(1)}
				</span>
			) : null}
		</div>
	);
}

export default function Avaliations() {
	const [loading, setLoading] = useState(false);
	const [pageNumber, setPageNumber] = useState(1);
	const [ratings, setRatings] = useState([]);
	const [hasMore, setHasMore] = useState(false);
	const [ratingsCount, setRatingsCount] = useState(0);
	const [filters, setFilters] = useState({
		searchParam: "",
		period: "3",
		user: "",
		queue: "",
		connection: "",
	});
	const [users, setUsers] = useState([]);
	const [queues, setQueues] = useState([]);
	const [connections, setConnections] = useState([]);

	const totalPages = Math.max(1, Math.ceil(ratingsCount / PAGE_SIZE));
	const rangeStart = ratingsCount ? (pageNumber - 1) * PAGE_SIZE + 1 : 0;
	const rangeEnd = Math.min(pageNumber * PAGE_SIZE, ratingsCount);

	const pageNumbers = () => {
		if (totalPages <= 1) return [1];

		let from = Math.max(1, pageNumber - 2);
		let to = Math.min(totalPages, from + 4);

		if (to - from < 4) {
			from = Math.max(1, to - 4);
		}

		return Array.from({ length: to - from + 1 }, (_, index) => from + index);
	};

	useEffect(() => {
		(async () => {
			try {
				const [
					{ data: usersData },
					{ data: queuesData },
					{ data: connectionsData },
				] = await Promise.all([
					api.get("/users/"),
					api.get("/queue/"),
					api.get("/whatsapp/"),
				]);

				setUsers(Array.isArray(usersData?.users) ? usersData.users : []);
				setQueues(Array.isArray(queuesData) ? queuesData : []);
				setConnections(Array.isArray(connectionsData) ? connectionsData : []);
			} catch (err) {
				toastError(err);
			}
		})();
	}, []);

	useEffect(() => {
		setPageNumber(1);
	}, [filters]);

	useEffect(() => {
		let active = true;
		setLoading(true);

		const debounce = setTimeout(async () => {
			try {
				const { data } = await api.get("/rating/", {
					params: { filters: JSON.stringify(filters), pageNumber },
				});

				if (!active) return;

				const nextRatings = Array.isArray(data?.tickets) ? data.tickets : [];
				setRatings(nextRatings);
				setHasMore(Boolean(data?.hasMore));
				setRatingsCount(Number(data?.count || 0));
			} catch (err) {
				if (active) toastError(err);
			} finally {
				if (active) setLoading(false);
			}
		}, 500);

		return () => {
			active = false;
			clearTimeout(debounce);
		};
	}, [pageNumber, filters]);

	const handleFilterChange = (event) => {
		const { name, value } = event.target;
		setFilters((prev) => ({ ...prev, [name]: value }));
	};

	const averageRating = useMemo(() => {
		const detractors = ratings.filter((rating) => rating.rating < 3).length;
		const promoters = ratings.filter((rating) => rating.rating > 3).length;
		const neutrals = ratings.filter((rating) => rating.rating === 3).length;
		const total = promoters + detractors + neutrals;

		if (!total) return "0%";

		const detractorsPercentage = (detractors * 100) / total;
		const promotersPercentage = (promoters * 100) / total;
		return `${(promotersPercentage - detractorsPercentage).toFixed(1)}%`;
	}, [ratings]);

	const summaryMetrics = useMemo(() => {
		const totalRatings = ratings.length;
		const ratingSum = ratings.reduce(
			(acc, rating) => acc + Number(rating.rating || 0),
			0
		);
		const promoters = ratings.filter((rating) => Number(rating.rating) >= 4).length;
		const detractors = ratings.filter((rating) => Number(rating.rating) <= 2).length;
		const comments = ratings.filter((rating) =>
			Boolean(rating.ratingDescription?.trim())
		).length;

		return {
			averageScore: totalRatings ? (ratingSum / totalRatings).toFixed(1) : "0.0",
			promoters,
			detractors,
			comments,
		};
	}, [ratings]);

	const emojiPercentages = useMemo(() => {
		const counters = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
		ratings.forEach((rating) => {
			if (counters[rating.rating] !== undefined) counters[rating.rating] += 1;
		});

		return RATING_OPTIONS.map((ratingOption) => ({
			...ratingOption,
			total: counters[ratingOption.value],
			percentageNumber: ratings.length
				? Number(
						((counters[ratingOption.value] / ratings.length) * 100).toFixed(1)
				  )
				: 0,
			percentage: ratings.length
				? ((counters[ratingOption.value] / ratings.length) * 100).toFixed(1)
				: "0.0",
		}));
	}, [ratings]);

	const attendantInsights = useMemo(() => {
		const grouped = ratings.reduce((acc, rating) => {
			const name = rating.user?.name || "Não atribuído";
			if (!acc[name]) {
				acc[name] = { name, count: 0, total: 0 };
			}
			acc[name].count += 1;
			acc[name].total += Number(rating.rating || 0);
			return acc;
		}, {});

		return Object.values(grouped)
			.map((item) => ({
				name: item.name,
				value: Number((item.total / item.count || 0).toFixed(1)),
				count: item.count,
			}))
			.sort((a, b) => b.value - a.value || b.count - a.count)
			.slice(0, 5);
	}, [ratings]);

	const queueInsights = useMemo(() => {
		const grouped = ratings.reduce((acc, rating) => {
			const name = rating.queue?.name || "Sem setor";
			acc[name] = (acc[name] || 0) + 1;
			return acc;
		}, {});

		return Object.entries(grouped)
			.map(([name, value]) => ({ name, value }))
			.sort((a, b) => b.value - a.value)
			.slice(0, 5);
	}, [ratings]);

	return (
		<MainContainer>
			<div className="app-admin-page">
				<div className="app-admin-page__scroll">
					<AppPageTitle title="Avaliações" subtitle="Atendimento" />

					<div className="card mb-5">
						<div className="card-body">
							<div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
								<FilterField label="Pesquisar">
									<div className="relative">
										<input
											type="search"
											name="searchParam"
											value={filters.searchParam}
											onChange={handleFilterChange}
											className="form-input w-full ps-11"
											placeholder="Nome do contato"
										/>
										<div className="pointer-events-none absolute inset-y-0 start-0 flex items-center ps-3">
											<IconSearch />
										</div>
									</div>
								</FilterField>

								<FilterField label="Período">
									<select
										name="period"
										value={filters.period}
										onChange={handleFilterChange}
										className="form-select w-full"
									>
										{PERIOD_OPTIONS.map((option) => (
											<option key={option.value} value={option.value}>
												{option.label}
											</option>
										))}
									</select>
								</FilterField>

								<FilterField label="Atendentes">
									<select
										name="user"
										value={filters.user}
										onChange={handleFilterChange}
										className="form-select w-full"
									>
										<option value="">Todos</option>
										{users.map((user) => (
											<option key={user.id} value={user.id}>
												{user.name}
											</option>
										))}
									</select>
								</FilterField>

								<FilterField label="Setores">
									<select
										name="queue"
										value={filters.queue}
										onChange={handleFilterChange}
										className="form-select w-full"
									>
										<option value="">Todos</option>
										{queues.map((queue) => (
											<option key={queue.id} value={queue.id}>
												{queue.name}
											</option>
										))}
									</select>
								</FilterField>

								<FilterField label="Conexões">
									<select
										name="connection"
										value={filters.connection}
										onChange={handleFilterChange}
										className="form-select w-full"
									>
										<option value="">Todos</option>
										{connections.map((connection) => (
											<option key={connection.id} value={connection.id}>
												{connection.name}
											</option>
										))}
									</select>
								</FilterField>
							</div>
						</div>
					</div>

					<div className="mb-5 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
						<OverviewMetricCard
							title="Total de respostas"
							value={ratingsCount}
							hint="Quantidade de avaliações encontradas com os filtros atuais."
							icon={<IconUsers />}
							toneClassName="bg-primary/10 text-primary"
						/>
						<OverviewMetricCard
							title="Pontuação de satisfação"
							value={averageRating}
							hint="Cálculo baseado em promotores menos detratores."
							icon={<IconSpark />}
							toneClassName="bg-warning/10 text-warning"
						/>
						<OverviewMetricCard
							title="Promotores"
							value={summaryMetrics.promoters}
							hint="Notas 4 e 5 nas avaliações carregadas."
							icon={<IconThumbUp />}
							toneClassName="bg-success/10 text-success"
						/>
						<OverviewMetricCard
							title="Com comentário"
							value={summaryMetrics.comments}
							hint="Clientes que deixaram feedback escrito."
							icon={<IconMessage />}
							toneClassName="bg-info/10 text-info"
						/>
					</div>

					<div className="mb-5 grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1fr)]">
						<SectionCard
							title="Distribuição das avaliações"
							subtitle={`Nota média atual: ${summaryMetrics.averageScore} de 5`}
						>
							<div className="mb-4 flex flex-wrap items-center justify-between gap-3">
								<div>
									<p className="mb-1 text-sm font-semibold text-default-800 dark:text-default-100">
										Como as notas estão distribuídas
									</p>
									<p className="m-0 text-sm text-default-500 dark:text-default-400">
										Percentual e volume por faixa de estrelas.
									</p>
								</div>
								<div className="text-end">
									<p className="m-0 text-[11px] font-semibold uppercase tracking-wide text-default-500 dark:text-default-400">
										Nota média
									</p>
									<p className="m-0 text-lg font-semibold text-primary">
										{summaryMetrics.averageScore}/5
									</p>
								</div>
							</div>

							<div className="relative grid grid-cols-2 gap-4 pt-2 lg:grid-cols-5">
								{emojiPercentages.map((emoji) => (
									<div key={emoji.value} className="flex flex-col items-center text-center">
										<p className="m-0 text-sm font-semibold text-default-800 dark:text-default-100">
											{emoji.percentage}%
										</p>
										<p className="mt-0.5 mb-0 text-[11px] text-default-500 dark:text-default-400">
											{emoji.total} resp.
										</p>

										<div className="relative mt-3 flex h-36 w-full items-end justify-center">
											<div className="absolute inset-x-2 bottom-0 h-px bg-default-200 dark:bg-default-700" />
											<div className="absolute inset-x-4 bottom-1/4 h-px bg-default-100 dark:bg-default-800" />
											<div className="absolute inset-x-4 bottom-2/4 h-px bg-default-100 dark:bg-default-800" />
											<div className="absolute inset-x-4 bottom-3/4 h-px bg-default-100 dark:bg-default-800" />
											<div className="relative z-10 flex h-full w-14 items-end">
												<div
													className="w-full rounded-t-2xl bg-gradient-to-t from-primary to-primary/65 transition-all"
													style={{
														height: `${Math.max(
															emoji.percentageNumber,
															emoji.total > 0 ? 10 : 0
														)}%`,
													}}
												/>
											</div>
										</div>

										<div className="mt-3 flex flex-col items-center gap-1">
											<StarRatingDisplay value={emoji.value} size="size-3" />
											<p className="m-0 text-[11px] leading-4 text-default-500 dark:text-default-400">
												{emoji.label}
											</p>
										</div>
									</div>
								))}
							</div>
						</SectionCard>

						<RankingCard
							title="Melhores médias por atendente"
							subtitle="Ranking baseado nas avaliações carregadas."
							data={attendantInsights}
							emptyMessage="Ainda não há atendentes com avaliações neste filtro."
							formatter={(item) => `${item.value.toFixed(1)} / 5`}
						/>

						<RankingCard
							title="Setores com mais avaliações"
							subtitle="Volume de respostas por setor."
							data={queueInsights}
							emptyMessage="Ainda não há setores com avaliações neste filtro."
							formatter={(item) => `${item.value} resposta(s)`}
						/>
					</div>

					{loading && ratings.length === 0 ? (
						<div className="app-modal-loading py-16">
							<div className="app-modal-spinner" />
						</div>
					) : ratings.length === 0 ? (
						<div className="card">
							<div className="card-body flex flex-col items-center justify-center gap-2 py-16 text-center">
								<p className="m-0 font-semibold text-default-800 dark:text-default-100">
									Nenhuma avaliação encontrada
								</p>
								<p className="m-0 text-sm text-default-500 dark:text-default-400">
									Ajuste os filtros ou aguarde novas respostas dos clientes.
								</p>
							</div>
						</div>
					) : (
						<div className="card">
							<div className="card-body p-0">
								<div className="app-table-scroll">
									<table className="w-full min-w-[1180px] table-auto">
										<thead>
											<tr className="bg-default-150">
												<th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-default-500">
													Contato
												</th>
												<th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-default-500">
													Ticket
												</th>
												<th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide text-default-500">
													Avaliação
												</th>
												<th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-default-500">
													Feedback
												</th>
												<th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-default-500">
													Atendente
												</th>
												<th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-default-500">
													Setor
												</th>
												<th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-default-500">
													Conexão
												</th>
												<th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-default-500">
													Data
												</th>
											</tr>
										</thead>
										<tbody>
											{ratings.map((rating) => {
												const ratingMeta = getRatingMeta(rating.rating);
												return (
													<tr key={rating.id}>
														<td className="px-4 py-3 text-sm text-default-700 dark:text-default-200">
															<div className="flex items-center gap-3">
																{rating.contact?.profilePicUrl ? (
																	<img
																		src={rating.contact.profilePicUrl}
																		alt={rating.contact?.name || "Contato"}
																		className="size-10 rounded-full object-cover"
																	/>
																) : (
																	<div className="flex size-10 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
																		{getInitials(rating.contact?.name)}
																	</div>
																)}
																<div className="min-w-0">
																	<p className="m-0 truncate font-medium text-default-800 dark:text-default-100">
																		{rating.contact?.name || "-"}
																	</p>
																	<p className="m-0 truncate text-xs text-default-500 dark:text-default-400">
																		{rating.contact?.number || "-"}
																	</p>
																</div>
															</div>
														</td>
														<td className="px-4 py-3 text-sm text-default-700 dark:text-default-200">
															#{rating.id}
														</td>
														<td className="px-4 py-3 text-center text-sm text-default-700 dark:text-default-200">
															<div className="inline-flex flex-col items-center gap-1">
																<StarRatingDisplay value={rating.rating} />
																<span className="text-xs text-default-500 dark:text-default-400">
																	{ratingMeta.label}
																</span>
															</div>
														</td>
														<td className="px-4 py-3 text-sm text-default-700 dark:text-default-200">
															<p className="m-0 line-clamp-3 max-w-[280px] whitespace-pre-wrap break-words">
																{rating.ratingDescription || "-"}
															</p>
														</td>
														<td className="px-4 py-3 text-sm text-default-700 dark:text-default-200">
															{rating.user?.name || "-"}
														</td>
														<td className="px-4 py-3 text-sm text-default-700 dark:text-default-200">
															{rating.queue?.name || "-"}
														</td>
														<td className="px-4 py-3 text-sm text-default-700 dark:text-default-200">
															{rating.whatsapp?.name || "-"}
														</td>
														<td className="px-4 py-3 text-sm text-default-700 dark:text-default-200">
															{formatDateTime(rating.updatedAt)}
														</td>
													</tr>
												);
											})}
										</tbody>
									</table>
								</div>
							</div>

							{ratingsCount > 0 && (
								<div className="card-footer flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
									<p className="m-0 text-sm text-default-500 dark:text-default-400">
										Exibindo <b>{rangeStart}</b> a <b>{rangeEnd}</b> de{" "}
										<b>{ratingsCount}</b> avaliação(ões)
									</p>
									{totalPages > 1 && (
										<nav
											className="flex flex-wrap items-center gap-2"
											aria-label="Paginação de avaliações"
										>
											<button
												type="button"
												className="btn btn-sm shrink-0 border border-default-200 bg-transparent text-default-600 hover:border-primary/10 hover:bg-primary/10 hover:text-primary disabled:cursor-not-allowed disabled:opacity-60 dark:border-default-600 dark:text-default-300"
												disabled={pageNumber <= 1 || loading}
												onClick={() =>
													setPageNumber((prev) => Math.max(1, prev - 1))
												}
											>
												<IconChevronLeft className="size-4 me-1 shrink-0" />
												Anterior
											</button>

											{pageNumbers().map((page) => (
												<button
													key={page}
													type="button"
													className={`btn size-7.5 shrink-0 border ${
														page === pageNumber
															? "border-primary bg-primary text-white"
															: "border-default-200 bg-transparent text-default-600 hover:border-primary/10 hover:bg-primary/10 hover:text-primary dark:border-default-600 dark:text-default-300"
													}`}
													disabled={loading}
													onClick={() => setPageNumber(page)}
												>
													{page}
												</button>
											))}

											<button
												type="button"
												className="btn btn-sm shrink-0 border border-default-200 bg-transparent text-default-600 hover:border-primary/10 hover:bg-primary/10 hover:text-primary disabled:cursor-not-allowed disabled:opacity-60 dark:border-default-600 dark:text-default-300"
												disabled={pageNumber >= totalPages || loading || !hasMore}
												onClick={() =>
													setPageNumber((prev) => Math.min(totalPages, prev + 1))
												}
											>
												Próxima
												<IconChevronRight className="size-4 ms-1 shrink-0" />
											</button>
										</nav>
									)}
								</div>
							)}
						</div>
					)}
				</div>
			</div>
		</MainContainer>
	);
}
